import Title from '@cohort/components-xps/components/Title';
import type {Language} from '@cohort/shared/schema/common';
import {LANGUAGE_FLAGS} from '@cohort/shared/schema/common';
import {formatI18nLanguage} from '@cohort/shared/utils/localization';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {AppsConnectionSettings} from '@cohort/wallet/apps/AppsConnectionSettings';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import type {SelectOption} from '@cohort/wallet/components/forms/DropDownInput';
import {DropDownInput} from '@cohort/wallet/components/forms/DropDownInput';
import {AboutBrand} from '@cohort/wallet/components/space/AboutBrand';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {useUserLogin} from '@cohort/wallet/hooks/api/User';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useScreenSize from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import SpaceContentDivider from '@cohort/wallet/layouts/space/SpaceContentDivider';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {logoutUser} from '@cohort/wallet/lib/Authentication';
import {UserApi} from '@cohort/wallet/lib/Endpoints';
import {getImportNftRoute, getMerchantSpaceLoginRoute} from '@cohort/wallet/lib/Pages';
import {MemberHandleForm} from '@cohort/wallet/pages/space/settings/MemberHandleForm';
import {WalletAddressCopy} from '@cohort/wallet/pages/space/settings/WalletAddressCopy';
import {Power} from '@phosphor-icons/react';
import i18n from 'i18next';
import {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

export const SettingsPage = (): JSX.Element => {
  const {space, setTransitioning} = useSpaceContext();
  const merchant = useMerchantContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.settingsPage'});
  const {hasDarkBg, accentColor} = useThemeContext();
  const {isMobile} = useScreenSize();
  const location = useLocation();
  const navigate = useNavigate();

  const {data: user} = useUserLogin();
  const disableLogout = useAppStore(store => store.disableLogout);

  const patchUser = useApi(UserApi.patchUser);

  const [language, setLanguage] = useState<string>(formatI18nLanguage(i18n.language));

  const {mutate: updateUser} = useCohortMutation({
    mutationFn: patchUser,
    onSuccess: () => window.location.reload(),
  });

  useEffect(() => {
    if (user) {
      setLanguage(user.lang);
    }
  }, [user]);

  if (space === undefined || !user) {
    return <Fragment />;
  }

  // i18nOwl-ignore [languages.ar, languages.cs, languages.de, languages.el]
  // i18nOwl-ignore [languages.en, languages.es, languages.fa, languages.fr]
  // i18nOwl-ignore [languages.id, languages.it, languages.ja, languages.ko]
  // i18nOwl-ignore [languages.nl, languages.pl, languages.pt, languages.ru]
  // i18nOwl-ignore [languages.tr, languages.uk, languages.vi, languages.zh]

  const languageOptions: SelectOption[] = merchant.supportedLanguages.map((language: Language) => {
    return {
      value: language,
      label: `${LANGUAGE_FLAGS[language]} ${t(`languages.${language}`)}`,
    };
  });

  const currentLanguage = ((): SelectOption => {
    const lang = languageOptions.find(option => option.value === language);
    return lang ?? (languageOptions[0] as SelectOption);
  })();

  return (
    <SpacePage
      title={t('settings')}
      leftPanel={<AboutBrand />}
      tracking={{
        namespace: 'settings',
      }}
    >
      <div className="flex flex-col">
        <Title content={t('settings')} classes="text-2xl" />
        <div
          className={classNames('text-sm italic', hasDarkBg ? 'text-white/60' : 'text-black/60')}
        >
          {user.email}
        </div>

        <div className="mt-6 md:mt-10">
          <MemberHandleForm user={user} />
        </div>
        <SpaceContentDivider />
        <WalletAddressCopy
          address={user.wallet.address}
          handleImportNftClick={() => {
            setTransitioning({
              active: true,
              enterFrom: isMobile ? 'bottom' : 'left',
            });
            navigate(getImportNftRoute());
          }}
        />
        <SpaceContentDivider />
        <Title content={t('language')} classes="text-lg" />
        <DropDownInput
          className="mt-2 md:w-[240px]"
          value={currentLanguage}
          options={merchant.supportedLanguages.map((language: Language) => {
            return {
              value: language,
              label: `${LANGUAGE_FLAGS[language]} ${t(`languages.${language}`)}`,
            };
          })}
          onChange={async (selectedValue: SelectOption) => {
            if (selectedValue.value !== language) {
              setLanguage(selectedValue.value);
              updateUser({lang: selectedValue.value as Language});
            }
          }}
        />

        <SpaceContentDivider />
        <Title content={t('apps')} classes="text-lg" />
        <AppsConnectionSettings />

        {!disableLogout && (
          <div className="mt-16 text-center lg:hidden">
            <SecondaryButton
              className="px-8"
              text={
                <div className="flex items-center gap-2">
                  <Power size={16} style={{color: accentColor}} />
                  {t('logout')}
                </div>
              }
              onClick={async () => {
                await logoutUser();
                navigate({
                  pathname: getMerchantSpaceLoginRoute(),
                  search: location.search,
                });
              }}
              testId="about-brand-logout-btn"
              type="button"
              tracking={{namespace: 'settings.logout'}}
            />
          </div>
        )}
      </div>
    </SpacePage>
  );
};
